import { Button, Text, Modal, LoadingAnimation, Icon } from '@/components'
import { AlertMessage } from '@/app/components'
import TextFormField from '@/components/forms/text-form-field'
import { useViewModel } from './accountNumberViewModel'
import { AnimatePresence, motion } from 'motion/react'
import { match } from 'ts-pattern'
import toExternalLink from '@/utils/to-external-link'
import { getValidUrl } from '@/utils/objectUtilities'

function UtilityBillExampleModal(props: {
  src: string
  open: boolean
  onClose: () => void
}) {
  return (
    <Modal
      id="utility-bill-example-modal"
      title="Utility bill example"
      open={props.open}
      onClose={props.onClose}
    >
      <div className="mt-14 max-h-96">
        <img src={props.src} alt="Utility bill example" className="w-full" />
      </div>
    </Modal>
  )
}

export default function AccountNumber() {
  const {
    fileInputRef,
    handleFilesUploaded,
    handleUploadClick,
    fileUploadStatus,
    fileUploadName,
    uploadErrorMessage,
    extractedAccountNumber,
    formik,
    register,
    accountNumberLength,
    accountNumberLabel,
    utilityBillExampleSrc,
    utilityWebsiteLink,
    utilityBillExampleModalOpen,
    setUtilityBillExampleModalOpen,
    handleNext,
    canProceed,
  } = useViewModel()

  const uploadAreaBorderColor = match(fileUploadStatus)
    .with('error', () => 'border-red-500')
    .with('success', () => 'border-green-500')
    .otherwise(() => 'border-gray-300')

  return (
    <div className="h-full flex flex-col gap-4">
      <section className="flex flex-col gap-1">
        <Text variant="h3">Enter account number</Text>
        <Text variant="body2">
          Upload a utility bill and we'll find your utility account number
          automatically or enter it manually. You can find it on your bill or on
          your{' '}
          {utilityWebsiteLink ? (
            <a
              href={toExternalLink(getValidUrl(utilityWebsiteLink))}
              target="_blank"
              rel="noreferrer"
              className="text-link !underline !text-themed-blue-500 cursor-pointer"
            >
              utility's website
            </a>
          ) : (
            <Text>utility's website</Text>
          )}
          .
        </Text>
      </section>

      <section className="flex flex-col gap-4">
        <div>
          <Text variant="body1" className="font-bold mb-4">
            Option 1: Upload document
          </Text>
          <div
            className={`border-[1px] border-solid ${uploadAreaBorderColor} rounded-lg p-8 flex flex-col items-center justify-center gap-4 cursor-pointer`}
            onClick={handleUploadClick}
          >
            <div
              className={`flex flex-col items-center rounded-lg p-2 ${
                fileUploadStatus === 'loading' ? '' : 'bg-themed-grey-100'
              }`}
            >
              {fileUploadStatus === 'loading' ? (
                <LoadingAnimation type="falling" size="small" />
              ) : (
                <Icon name="Upload" size={24} color="grey-500" />
              )}
            </div>
            <div className="flex flex-col items-center gap-1">
              <Text variant="subheader">
                {fileUploadStatus === 'loading'
                  ? 'Processing utility bill'
                  : 'Upload utility bill'}
              </Text>
              <Text variant="body2" className="text-themed-grey-500">
                Supports PDF, JPG, PNG (Max 10MB)
              </Text>
            </div>
          </div>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            ref={fileInputRef}
            onChange={handleFilesUploaded}
            className="hidden"
          />

          <AnimatePresence>
            {fileUploadStatus === 'success' && fileUploadName && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-4"
              >
                <AlertMessage variant="success">
                  <Text className="text-initial">
                    {fileUploadName} uploaded successfully.
                  </Text>
                  <Text variant="label" className="mt-1 text-initial font-bold">
                    Account #: {extractedAccountNumber}
                  </Text>
                  <Text variant="body2" className="mt-2 text-initial">
                    Bill successfully uploaded and we extracted your utility
                    account number and filled it in below.
                  </Text>
                </AlertMessage>
              </motion.div>
            )}
            {fileUploadStatus === 'error' && uploadErrorMessage && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-4"
              >
                <AlertMessage variant="error">
                  <div className="flex flex-col gap-1">
                    <Text className="body3 text-initial text-red-500 font-bold">
                      {uploadErrorMessage.title}
                    </Text>
                    <Text className="body2 text-initial text-red-500 !font-medium">
                      {uploadErrorMessage.subtitle}
                    </Text>
                  </div>
                </AlertMessage>
              </motion.div>
            )}
          </AnimatePresence>

          {fileUploadStatus !== 'success' && (
            <div className="mt-4">
              <AlertMessage variant="info">
                <div className="flex flex-col gap-1">
                  Make sure your utility account number is clearly visible in
                  the document.
                  {utilityBillExampleSrc && (
                    <Text
                      variant="link"
                      className="text-themed-blue-500 cursor-pointer mt-1"
                      onClick={() => setUtilityBillExampleModalOpen(true)}
                    >
                      View bill example
                    </Text>
                  )}
                </div>
              </AlertMessage>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          <div className="flex-1 border-solid border-t border-gray-300"></div>
          <Text variant="body2" className="text-themed-grey-500">
            OR
          </Text>
          <div className="flex-1 border-solid border-t border-gray-300"></div>
        </div>

        <div>
          <Text variant="body1" className="font-bold mb-4">
            Option 2: Enter manually
          </Text>
          <div className="max-w-md w-full">
            <TextFormField
              name="accountNumber"
              label={accountNumberLabel}
              type="text"
              placeholder="Enter account number"
              register={register}
              value={formik.values.accountNumber}
              error={
                formik.errors.accountNumber && formik.touched.accountNumber
                  ? formik.errors.accountNumber
                  : undefined
              }
              showErrorDropdown={Boolean(
                formik.errors.accountNumber && formik.touched.accountNumber,
              )}
            />
            {!formik.errors.accountNumber && accountNumberLength && (
              <AnimatePresence initial={false}>
                <motion.div
                  key="helper-text"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  <Text
                    variant="body3"
                    className="text-themed-grey-500 pt-2 font-medium"
                  >
                    Your {accountNumberLabel} must be {accountNumberLength}{' '}
                    characters, not including spaces
                  </Text>
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </section>

      {utilityBillExampleSrc && (
        <UtilityBillExampleModal
          src={utilityBillExampleSrc}
          open={utilityBillExampleModalOpen}
          onClose={() => setUtilityBillExampleModalOpen(false)}
        />
      )}

      <div className="mt-auto">
        <Button
          id="account-number-next-button"
          variant="primary"
          disabled={!canProceed}
          onClick={handleNext}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
